import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { IAggregateResult, BucketKey, IDailyStat } from '../../lib/mockupLogsDashboard/types';
import {
  drawColumns,
  drawDonut,
  drawDonutCompare,
  drawLine,
  drawStackedBars,
  drawSpark,
  type IColumnLayer,
  type ILineSeries,
  type ISlice,
  type IStackGroup,
} from '../../lib/mockupLogsDashboard/charts';
import { alignComparison, delta, deltaBadgeClass, fmt, fmtPct, fmtSec, shortDate } from '../../lib/mockupLogsDashboard/utils';
import type { DashboardPalette } from '../../lib/mockupLogsDashboard/palette';

interface Props {
  curr: IAggregateResult;
  prev: IAggregateResult | null;
  resizeTick?: number;
  themeMode?: 'light' | 'dark';
  palette: DashboardPalette;
}

function speedPill(catObj: Record<BucketKey, number>): { cls: string; text: string } {
  const slow = (catObj.slow || 0) + (catObj.extraSlow || 0);
  if (slow > 15) return { cls: 'danger', text: `${slow.toFixed(1)}% slow` };
  if (slow > 8) return { cls: 'warn', text: `${slow.toFixed(1)}% slow` };
  return { cls: '', text: `${(catObj.fast || 0).toFixed(1)}% fast` };
}

function DeltaBadge({ curr, prev, inverted }: { curr: number; prev: number | null | undefined; inverted?: boolean }) {
  const d = delta(curr, prev ?? undefined);
  if (d === null || Number.isNaN(d)) return null;
  const cls = deltaBadgeClass(curr, prev ?? undefined, { inverted });
  const dir = d > 0.05 ? 'up' : d < -0.05 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–';
  return (
    <span className={`delta ${cls}`} title="vs. previous period">
      {arrow} {Math.abs(d).toFixed(1)}%
    </span>
  );
}

function DeltaInline({ curr, prev, inverted }: { curr: number; prev: number | null | undefined; inverted?: boolean }) {
  const d = delta(curr, prev ?? undefined);
  if (d === null || Number.isNaN(d)) return null;
  const cls = deltaBadgeClass(curr, prev ?? undefined, { inverted });
  const arrow = d > 0.05 ? '▲' : d < -0.05 ? '▼' : '–';
  return (
    <span className={`delta-inline ${cls}`}>
      {arrow}
      {Math.abs(d).toFixed(1)}%
    </span>
  );
}

function InfoTip({ text }: { text: string }) {
  return (
    <button type="button" className="info-btn" data-tip={text} aria-label={text}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="7" cy="4.2" r="0.85" fill="currentColor" />
        <path d="M7 6.5 V10.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    </button>
  );
}

const TIPS = {
  outcomes:
    'Events per day split by outcome: success, error and not ended (the customer left mid-flow). When compare is on, the lighter column beside each day is the matching day of the previous period.',
  dur: 'Daily average duration (seconds) per event, for each stage of the flow. Lower is better. When comparing, dashed lines show the previous period aligned to current day count.',
  failures:
    'Share of events that errored or were abandoned each day, stacked. The axis follows the data, so a 1–3% failure share stays readable; the success share is in the tooltip.',
  speedMix: 'Share of process-duration events in each speed bucket per day (fast / medium / slow / extra slow). More green = better; the previous period, if compared, is the lighter column.',
  process:
    'Distribution of process-duration events across speed buckets (fast / medium / slow / extra slow). Left bar = current period, right bar = previous period (when compare is on). More green = better.',
  mockup: 'Distribution of mockup-rendering events across speed buckets. Compares current vs previous when comparison is enabled.',
  save: 'Distribution of save-step events across speed buckets. Save is typically the fastest stage; watch for unusual share of slow/extra-slow.',
  status: 'Outer ring shows the current selection; inner ring shows the previous period (when compare is on). The center value is the success rate of the current selection.',
  err: 'Of all errors, how they break down by origin: errMockup (mockup generation), errRemovebg (background removal), errNetwork (transport / API).',
  ne: 'Of all sessions that did not finish, when users left the flow — under 10 seconds, between 10–20 seconds, or after 20 seconds.',
  tblProcess: 'For each bucket: % of events that fell into the bucket, plus the bucket-average duration (seconds). Δ = absolute change vs previous period.',
  tblMockup: 'Mockup-stage bucket shares and per-bucket average durations. Δ = absolute change vs previous period.',
  tblSave: 'Save-stage bucket shares and per-bucket average durations. Δ = absolute change vs previous period.',
  tblDaily: 'Per-day metrics for the current selection. Each row reflects one calendar day in the chosen range.',
};

function ToggleLegend({
  items,
  hidden,
  onToggle,
}: {
  items: readonly { name: string; color: string }[];
  hidden: Set<string>;
  onToggle: (name: string) => void;
}) {
  return (
    <div className="legend" style={{ marginTop: 6 }}>
      {items.map((item) => {
        const off = hidden.has(item.name);
        return (
          <button
            type="button"
            key={item.name}
            onClick={() => onToggle(item.name)}
            aria-pressed={!off}
            style={{ cursor: 'pointer', opacity: off ? 0.4 : 1, background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit' }}
            title={off ? 'Click to show' : 'Click to hide'}
          >
            <span className="swatch" style={off ? { background: 'transparent', border: `1.5px dashed ${item.color}` } : { background: item.color }} />
            <span style={{ textDecoration: off ? 'line-through' : 'none' }}>{item.name}</span>
          </button>
        );
      })}
    </div>
  );
}

const BUCKETS: BucketKey[] = ['fast', 'medium', 'slow', 'extraSlow'];
const BUCKET_LABELS: Record<BucketKey, string> = { fast: 'Fast', medium: 'Medium', slow: 'Slow', extraSlow: 'Extra slow' };

const num = (v: unknown): number => {
  const n = parseFloat(String(v ?? 0));
  return Number.isFinite(n) ? n : 0;
};

const MockupLogsPerformance: React.FC<Props> = ({ curr, prev, resizeTick = 0, themeMode = 'light', palette }) => {
  const e = curr.eventStats;
  const p = prev?.eventStats ?? null;
  const curDays = curr.days;
  const prevAligned = useMemo(() => (prev ? alignComparison(curDays, prev.days) : null), [curDays, prev]);

  const durSeries = useMemo(
    () => [
      { name: 'Process', color: palette.primary },
      { name: 'Mockup', color: palette.info },
      { name: 'Save', color: palette.success },
    ],
    [palette]
  );
  const [hiddenDur, setHiddenDur] = useState<Set<string>>(new Set());
  const toggleDur = (name: string) =>
    setHiddenDur((prevHidden) => {
      const s = new Set(prevHidden);
      if (s.has(name)) s.delete(name);
      else s.add(name);
      return s;
    });

  const refOutcomes = useRef<HTMLCanvasElement>(null);
  const refDur = useRef<HTMLCanvasElement>(null);
  const refFail = useRef<HTMLCanvasElement>(null);
  const refMix = useRef<HTMLCanvasElement>(null);
  const refProcess = useRef<HTMLCanvasElement>(null);
  const refMockup = useRef<HTMLCanvasElement>(null);
  const refSave = useRef<HTMLCanvasElement>(null);
  const refStatus = useRef<HTMLCanvasElement>(null);
  const refErr = useRef<HTMLCanvasElement>(null);
  const refNe = useRef<HTMLCanvasElement>(null);
  const sparkRefs = [useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null)];

  useLayoutEffect(() => {
    const labels = curDays.map((d) => shortDate(d.statDate));

    const outcomeLayers = (days: IDailyStat[]): IColumnLayer[] => [
      { key: 'Success', color: palette.success, data: days.map((d) => num(d.eventStats.endStatusCountCat?.success)) },
      { key: 'Error', color: palette.danger, data: days.map((d) => num(d.eventStats.endStatusCountCat?.error)) },
      { key: 'Not ended', color: palette.warning, data: days.map((d) => num(d.eventStats.endStatusCountCat?.notEnded)) },
    ];
    if (refOutcomes.current) {
      drawColumns(refOutcomes.current, labels, outcomeLayers(curDays), {
        prev: prevAligned ? outcomeLayers(prevAligned) : null,
        extraRows: [{ label: 'Success rate', color: palette.success, values: curDays.map((d) => fmtPct(num(d.eventStats.endStatusPctCat?.success), 2)) }],
      });
    }

    const durDataFns: Record<string, (d: IDailyStat) => number> = {
      Process: (d) => num(d.eventStats.processDurationAvg),
      Mockup: (d) => num(d.eventStats.actualMockupDurationAvg),
      Save: (d) => num(d.eventStats.saveDurationAvg),
    };
    const dSer: ILineSeries[] = durSeries
      .filter((s) => !hiddenDur.has(s.name))
      .map((s) => ({ name: s.name, color: s.color, data: curDays.map((d) => ({ x: shortDate(d.statDate), y: durDataFns[s.name](d) })) }));
    if (prevAligned) {
      durSeries
        .filter((s) => !hiddenDur.has(s.name))
        .forEach((s) => {
          dSer.push({
            name: s.name + ' (prev)',
            color: s.color,
            dashed: true,
            data: prevAligned.map((d, i) => ({ x: shortDate(curDays[i].statDate), y: durDataFns[s.name](d) })),
          });
        });
    }
    if (refDur.current) drawLine(refDur.current, dSer, { yFmt: (v) => v.toFixed(2) + 's' });

    const failLayers = (days: IDailyStat[]): IColumnLayer[] => [
      { key: 'Error', color: palette.danger, data: days.map((d) => num(d.eventStats.endStatusPctCat?.error)) },
      { key: 'Not ended', color: palette.warning, data: days.map((d) => num(d.eventStats.endStatusPctCat?.notEnded)) },
    ];
    if (refFail.current) {
      drawColumns(refFail.current, labels, failLayers(curDays), {
        prev: prevAligned ? failLayers(prevAligned) : null,
        yFmt: (v) => v.toFixed(1) + '%',
        extraRows: [
          { label: 'Success', color: palette.success, values: curDays.map((d) => fmtPct(num(d.eventStats.endStatusPctCat?.success), 2)) },
          { label: 'Events', color: palette.neutral, values: curDays.map((d) => fmt(d.eventStats.eventCount)) },
        ],
      });
    }

    const mixLayers = (days: IDailyStat[]): IColumnLayer[] =>
      BUCKETS.map((b) => ({ key: BUCKET_LABELS[b], color: palette.bucket[b], data: days.map((d) => num(d.eventStats.processDurationPctCat?.[b])) }));
    if (refMix.current) {
      drawColumns(refMix.current, labels, mixLayers(curDays), {
        maxValue: 100,
        yFmt: (v) => v.toFixed(0) + '%',
        prev: prevAligned ? mixLayers(prevAligned) : null,
        extraRows: [{ label: 'Process avg', color: palette.primary, values: curDays.map((d) => fmtSec(num(d.eventStats.processDurationAvg), 2)) }],
      });
    }

    function drawBucketCanvas(canvas: HTMLCanvasElement | null, key: 'processCat' | 'actualMockupCat' | 'saveCat') {
      if (!canvas) return;
      const ccur = e[key];
      const ppr = p ? p[key] : null;
      const stacks = BUCKETS.map((s) => ({ key: s, color: palette.bucket[s], data: [ccur[s]] }));
      const groups: IStackGroup[] = [{ label: 'Current', stacks }];
      if (ppr) {
        groups.push({ label: 'Previous', stacks: BUCKETS.map((s) => ({ key: s, color: palette.bucket[s], data: [ppr[s]] })) });
      }
      drawStackedBars(canvas, [' '], groups);
    }
    drawBucketCanvas(refProcess.current, 'processCat');
    drawBucketCanvas(refMockup.current, 'actualMockupCat');
    drawBucketCanvas(refSave.current, 'saveCat');

    const statusCur: ISlice[] = [
      { label: 'Success', value: e.success, color: palette.success, labelColor: palette.label.success },
      { label: 'Error', value: e.error, color: palette.danger, labelColor: palette.label.danger },
      { label: 'Not ended', value: e.notEnded, color: palette.warning, labelColor: palette.label.warning },
    ];
    if (p && refStatus.current) {
      drawDonutCompare(refStatus.current, statusCur, [{ value: p.success }, { value: p.error }, { value: p.notEnded }], {
        centerLabel: fmtPct(e.successPct, 1),
        centerSub: 'Success',
      });
    } else if (refStatus.current) {
      drawDonut(refStatus.current, statusCur, { donut: true, centerLabel: fmtPct(e.successPct, 1), centerSub: 'Success' });
    }

    const errCur: ISlice[] = [
      { label: 'errMockup', value: e.errMockup, color: palette.danger, labelColor: palette.label.danger },
      { label: 'errRemovebg', value: e.errRemovebg, color: palette.warning, labelColor: palette.label.warning },
      { label: 'errNetwork', value: e.errNetwork, color: palette.info, labelColor: palette.label.info },
    ];
    if (p && refErr.current) {
      drawDonutCompare(refErr.current, errCur, [{ value: p.errMockup }, { value: p.errRemovebg }, { value: p.errNetwork }]);
    } else if (refErr.current) {
      drawDonut(refErr.current, errCur);
    }

    const neCur: ISlice[] = [
      { label: '<10s', value: e.leftBefore10s, color: palette.neutral, labelColor: palette.label.neutral },
      { label: '10-20s', value: e.leftAfter10s, color: palette.secondary, labelColor: palette.label.secondary },
      { label: '>20s', value: e.leftAfter20s, color: palette.info, labelColor: palette.label.info },
    ];
    if (p && refNe.current) {
      drawDonutCompare(refNe.current, neCur, [{ value: p.leftBefore10s }, { value: p.leftAfter10s }, { value: p.leftAfter20s }]);
    } else if (refNe.current) {
      drawDonut(refNe.current, neCur);
    }
  }, [curr, prev, e, p, curDays, prevAligned, resizeTick, hiddenDur, themeMode, palette, durSeries]);

  const sparkSucc = useMemo(() => curDays.map((d) => num(d.eventStats.endStatusPctCat?.success)), [curDays]);
  const sparkEv = useMemo(() => curDays.map((d) => d.eventStats.eventCount || 0), [curDays]);
  const sparkProc = useMemo(() => curDays.map((d) => num(d.eventStats.processDurationAvg)), [curDays]);
  const sparkErr = useMemo(() => curDays.map((d) => num(d.eventStats.endStatusPctCat?.error)), [curDays]);

  useLayoutEffect(() => {
    const cards = [
      { spark: sparkSucc, color: palette.success },
      { spark: sparkEv, color: palette.primary },
      { spark: sparkProc, color: palette.warning },
      { spark: sparkErr, color: palette.danger },
    ];
    cards.forEach((c, i) => {
      const el = sparkRefs[i].current;
      if (el) drawSpark(el, c.spark, c.color);
    });
  }, [curDays, sparkSucc, sparkEv, sparkProc, sparkErr, resizeTick, themeMode, palette]); // eslint-disable-line react-hooks/exhaustive-deps

  const errRate = e.errorPct;
  const successCls = e.successPct >= 95 ? 'tinted-success' : e.successPct >= 90 ? 'tinted-warn' : 'tinted-danger';
  const errCls = errRate < 2 ? 'tinted-success' : errRate < 4 ? 'tinted-warn' : 'tinted-danger';

  const sp = speedPill(e.processCat);
  const sm = speedPill(e.actualMockupCat);
  const ss = speedPill(e.saveCat);

  const hasPrev = !!p;

  const bucketLegend = (cat: Record<BucketKey, number>) => (
    <div className="legend">
      {BUCKETS.map((b) => (
        <span key={b}>
          <span className="swatch" style={{ background: palette.bucket[b] }} />
          {BUCKET_LABELS[b]}
          {b === 'fast' ? ' (<5s)' : ''} <strong>{fmtPct(cat[b], 1)}</strong>
        </span>
      ))}
    </div>
  );

  const prevLegend = prevAligned ? (
    <span style={{ opacity: 0.7 }}>
      <span className="swatch" style={{ background: palette.prev, opacity: 0.6 }} />
      Previous period (lighter column)
    </span>
  ) : null;

  return (
    <>
      <div className="section-title">Key indicators</div>
      <div className="grid grid-4" id="kpi-row">
        <div className={`card kpi ${successCls}`}>
          <div className="kpi-label">Success rate</div>
          <div className="kpi-value">
            {fmtPct(e.successPct, 2)} {p ? <DeltaBadge curr={e.successPct} prev={p.successPct} /> : null}
          </div>
          <div className="kpi-sub">
            Error {fmtPct(e.errorPct, 2)} · Not ended {fmtPct(e.notEndedPct, 2)}
          </div>
          <canvas ref={sparkRefs[0]} className="spark" aria-hidden="true" />
        </div>
        <div className="card kpi">
          <div className="kpi-label">Events</div>
          <div className="kpi-value">
            {fmt(e.eventCount)} {p ? <DeltaBadge curr={e.eventCount} prev={p.eventCount} /> : null}
          </div>
          <div className="kpi-sub">
            {fmt(e.success)} success ({fmtPct(e.successPct, 1)}) · {fmt(e.error)} errors ({fmtPct(e.errorPct, 1)})
          </div>
          <canvas ref={sparkRefs[1]} className="spark" aria-hidden="true" />
        </div>
        <div className="card kpi tinted-warn">
          <div className="kpi-label">Process duration avg</div>
          <div className="kpi-value">
            {fmtSec(e.processDurationAvg, 2)} {p ? <DeltaBadge curr={e.processDurationAvg} prev={p.processDurationAvg} inverted /> : null}
          </div>
          <div className="kpi-sub">
            Mockup {fmtSec(e.mockupDurationAvg, 2)} · Save {fmtSec(e.saveDurationAvg, 2)}
          </div>
          <canvas ref={sparkRefs[2]} className="spark" aria-hidden="true" />
        </div>
        <div className={`card kpi ${errCls}`}>
          <div className="kpi-label">Error rate</div>
          <div className="kpi-value">
            {fmtPct(errRate, 2)} {p ? <DeltaBadge curr={errRate} prev={p.errorPct} inverted /> : null}
          </div>
          <div className="kpi-sub">
            {fmt(e.error)} of {fmt(e.eventCount)} events
          </div>
          <canvas ref={sparkRefs[3]} className="spark" aria-hidden="true" />
        </div>
      </div>

      <div className="section-title">Daily trends</div>
      <div className="grid grid-2">
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Events by outcome</h3>
                <InfoTip text={TIPS.outcomes} />
              </div>
              <div className="sub">Uploads per day: success, error, not ended</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 240 }}>
            <canvas ref={refOutcomes} role="img" aria-label="Events per day by outcome" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span><span className="swatch" style={{ background: palette.success }} />Success</span>
            <span><span className="swatch" style={{ background: palette.danger }} />Error</span>
            <span><span className="swatch" style={{ background: palette.warning }} />Not ended</span>
            {prevLegend}
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Average durations</h3>
                <InfoTip text={TIPS.dur} />
              </div>
              <div className="sub">Per-event averages, in seconds</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 240 }}>
            <canvas ref={refDur} role="img" aria-label="Average durations per day" style={{ width: '100%', height: '100%' }} />
          </div>
          <ToggleLegend items={durSeries} hidden={hiddenDur} onToggle={toggleDur} />
        </div>
      </div>
      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Failures per day</h3>
                <InfoTip text={TIPS.failures} />
              </div>
              <div className="sub">Share of events that errored or were abandoned</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            <canvas ref={refFail} role="img" aria-label="Failure share per day" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span><span className="swatch" style={{ background: palette.danger }} />Error %</span>
            <span><span className="swatch" style={{ background: palette.warning }} />Not ended %</span>
            {prevLegend}
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Process speed mix per day</h3>
                <InfoTip text={TIPS.speedMix} />
              </div>
              <div className="sub">Share of events per speed bucket</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            <canvas ref={refMix} role="img" aria-label="Process speed mix per day" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            {BUCKETS.map((b) => (
              <span key={b}><span className="swatch" style={{ background: palette.bucket[b] }} />{BUCKET_LABELS[b]}</span>
            ))}
            {prevLegend}
          </div>
        </div>
      </div>

      <div className="section-title">Speed distribution</div>
      <div className="grid grid-3">
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Process duration</h3>
                <InfoTip text={TIPS.process} />
              </div>
              <div className="sub">Bucket share, current vs previous</div>
            </div>
            <span className={`pill ${sp.cls}`}>
              <span className="dot" />
              {sp.text}
            </span>
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            <canvas ref={refProcess} role="img" aria-label="Process duration bucket share" style={{ width: '100%', height: '100%' }} />
          </div>
          {bucketLegend(e.processCat)}
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Mockup duration</h3>
                <InfoTip text={TIPS.mockup} />
              </div>
              <div className="sub">Bucket share, current vs previous</div>
            </div>
            <span className={`pill ${sm.cls}`}>
              <span className="dot" />
              {sm.text}
            </span>
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            <canvas ref={refMockup} role="img" aria-label="Mockup duration bucket share" style={{ width: '100%', height: '100%' }} />
          </div>
          {bucketLegend(e.actualMockupCat)}
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Save duration</h3>
                <InfoTip text={TIPS.save} />
              </div>
              <div className="sub">Bucket share, current vs previous</div>
            </div>
            <span className={`pill ${ss.cls}`}>
              <span className="dot" />
              {ss.text}
            </span>
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            <canvas ref={refSave} role="img" aria-label="Save duration bucket share" style={{ width: '100%', height: '100%' }} />
          </div>
          {bucketLegend(e.saveCat)}
        </div>
      </div>

      <div className="grid grid-3" style={{ marginTop: 14 }}>
        <BucketTable title="Process — avg by bucket" catKey="processCat" avgKey="processBucketAvg" curr={e} prev={p} tip={TIPS.tblProcess} hasPrev={hasPrev} />
        <BucketTable title="Mockup — avg by bucket" catKey="actualMockupCat" avgKey="actualMockupBucketAvg" curr={e} prev={p} tip={TIPS.tblMockup} hasPrev={hasPrev} />
        <BucketTable title="Save — avg by bucket" catKey="saveCat" avgKey="saveBucketAvg" curr={e} prev={p} tip={TIPS.tblSave} hasPrev={hasPrev} />
      </div>

      <div className="section-title">Reliability breakdown</div>
      <div className="grid grid-3">
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Status share</h3>
                <InfoTip text={TIPS.status} />
              </div>
              <div className="sub">Outer ring = current; inner = previous</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 230 }}>
            <canvas ref={refStatus} role="img" aria-label="Status share" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span><span className="swatch" style={{ background: palette.success }} />Success <strong>{fmtPct(e.successPct, 1)}</strong></span>
            <span><span className="swatch" style={{ background: palette.danger }} />Error <strong>{fmtPct(e.errorPct, 1)}</strong></span>
            <span><span className="swatch" style={{ background: palette.warning }} />Not ended <strong>{fmtPct(e.notEndedPct, 1)}</strong></span>
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Error breakdown</h3>
                <InfoTip text={TIPS.err} />
              </div>
              <div className="sub">Failures by origin</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 230 }}>
            <canvas ref={refErr} role="img" aria-label="Error breakdown" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span><span className="swatch" style={{ background: palette.danger }} />errMockup <strong>{fmtPct(e.errMockupPct, 1)}</strong></span>
            <span><span className="swatch" style={{ background: palette.warning }} />errRemovebg <strong>{fmtPct(e.errRemovebgPct, 1)}</strong></span>
            <span><span className="swatch" style={{ background: palette.info }} />errNetwork <strong>{fmtPct(e.errNetworkPct, 1)}</strong></span>
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Abandoned (Not ended)</h3>
                <InfoTip text={TIPS.ne} />
              </div>
              <div className="sub">When users left mid-flow</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 230 }}>
            <canvas ref={refNe} role="img" aria-label="Abandoned sessions by time left" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span><span className="swatch" style={{ background: palette.neutral }} />&lt;10s <strong>{fmtPct(e.leftBefore10sPct, 1)}</strong></span>
            <span><span className="swatch" style={{ background: palette.secondary }} />10–20s <strong>{fmtPct(e.leftAfter10sPct, 1)}</strong></span>
            <span><span className="swatch" style={{ background: palette.info }} />&gt;20s <strong>{fmtPct(e.leftAfter20sPct, 1)}</strong></span>
          </div>
        </div>
      </div>

      <div className="section-title">
        Daily breakdown <InfoTip text={TIPS.tblDaily} />
      </div>
      <div className="card table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Date</th>
              <th className="num">Events</th>
              <th className="num">Success%</th>
              <th className="num">Error%</th>
              <th className="num">Not ended%</th>
              <th className="num">Process avg</th>
              <th className="num">Mockup avg</th>
              <th className="num">Save avg</th>
              <th>Speed mix (process)</th>
            </tr>
          </thead>
          <tbody>
            {curDays.map((d, i) => {
              const ev = d.eventStats;
              const c = ev.processDurationPctCat || { fast: '0', medium: '0', slow: '0', extraSlow: '0' };
              const errP = num(ev.endStatusPctCat?.error);
              const prevRow = prevAligned && prevAligned[i];
              const pc = prevRow ? prevRow.eventStats.processDurationPctCat || {} : {};
              return (
                <React.Fragment key={d.statDate}>
                  <tr>
                    <td>
                      <strong>{d.statDate}</strong>
                    </td>
                    <td className="num">{fmt(ev.eventCount || 0)}</td>
                    <td className="num">
                      <span className="tag fast">{ev.endStatusPctCat?.success}%</span>
                    </td>
                    <td className="num">
                      <span className={`tag ${errP > 2.5 ? 'extraSlow' : 'slow'}`}>{ev.endStatusPctCat?.error}%</span>
                    </td>
                    <td className="num">{ev.endStatusPctCat?.notEnded}%</td>
                    <td className="num">{num(ev.processDurationAvg).toFixed(2)}s</td>
                    <td className="num">{num(ev.actualMockupDurationAvg).toFixed(2)}s</td>
                    <td className="num">{num(ev.saveDurationAvg).toFixed(2)}s</td>
                    <td>
                      <div className="stack" title="fast / medium / slow / extra slow">
                        {BUCKETS.map((b) => (
                          <span key={b} style={{ width: `${c[b]}%`, background: palette.bucket[b] }} />
                        ))}
                      </div>
                    </td>
                  </tr>
                  {prevRow && (
                    <tr className="prev">
                      <td>{prevRow.statDate}</td>
                      <td className="num">{fmt(prevRow.eventStats.eventCount || 0)}</td>
                      <td className="num">{prevRow.eventStats.endStatusPctCat?.success}%</td>
                      <td className="num">{prevRow.eventStats.endStatusPctCat?.error}%</td>
                      <td className="num">{prevRow.eventStats.endStatusPctCat?.notEnded}%</td>
                      <td className="num">{num(prevRow.eventStats.processDurationAvg).toFixed(2)}s</td>
                      <td className="num">{num(prevRow.eventStats.actualMockupDurationAvg).toFixed(2)}s</td>
                      <td className="num">{num(prevRow.eventStats.saveDurationAvg).toFixed(2)}s</td>
                      <td>
                        <div className="stack">
                          {BUCKETS.map((b) => (
                            <span key={b} style={{ width: `${(pc as Record<string, string>)[b] || 0}%`, background: palette.bucket[b], opacity: 0.5 }} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

function BucketTable({
  title,
  catKey,
  avgKey,
  curr,
  prev,
  tip,
  hasPrev,
}: {
  title: string;
  catKey: 'processCat' | 'actualMockupCat' | 'saveCat';
  avgKey: 'processBucketAvg' | 'actualMockupBucketAvg' | 'saveBucketAvg';
  curr: IAggregateResult['eventStats'];
  prev: IAggregateResult['eventStats'] | null;
  tip: string;
  hasPrev: boolean;
}) {
  const cat = curr[catKey];
  const avgCat = curr[avgKey];
  const pCat = prev ? prev[catKey] : null;
  const pAvg = prev ? prev[avgKey] : null;
  return (
    <div className="card">
      <div className="title-row" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13 }}>{title}</h3>
        <InfoTip text={tip} />
      </div>
      <div className="table-wrap" style={{ overflow: 'auto' }}>
        <table className="data">
          <thead>
            <tr>
              <th>Bucket</th>
              <th className="num">Share</th>
              <th className="num">Avg (s)</th>
            </tr>
          </thead>
          <tbody>
            {BUCKETS.map((b) => (
              <tr key={b}>
                <td>
                  <span className={`tag ${b}`}>{BUCKET_LABELS[b]}</span>
                </td>
                <td className="num">
                  {cat[b].toFixed(2)}%{hasPrev && pCat ? <DeltaInline curr={cat[b]} prev={pCat[b]} inverted={b !== 'fast'} /> : null}
                </td>
                <td className="num">
                  {avgCat[b] ? avgCat[b].toFixed(2) + 's' : '—'}
                  {hasPrev && pAvg && pAvg[b] ? <DeltaInline curr={avgCat[b]} prev={pAvg[b]} inverted /> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MockupLogsPerformance;
